import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	L2OrderBook,
	MarketStatus,
	MarketType,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	OpenPosition,
	UIOrderType,
} from '@velocity-exchange/common';
import {
	getDelistingTextNoPosition,
	isDelistingTimestampInFuture,
	isMarketReduceOnly,
	marketToBeDelisted,
} from 'src/utils/delisting';
import Button from 'src/components/primitives/Button/Button';
import {
	MAX_NUMBER_OF_PERP_POSITIONS,
	SUPER_STAKE_ACCOUNT_NAMES,
} from 'src/constants/constants';
import { getMessageInfoForOrderViolation, MessageInfo } from 'src/utils/trade';
import { MAX_SPOT_POSITIONS_ERROR_MESSAGE } from 'src/constants/markets';
import { DriftStore } from 'src/stores/DriftStore/useDriftStore';

const BASE_CASE = {
	shouldShowMessage: false,
	messageTitle: '',
	message: '',
	messageType: 'info' as const,
	skipMessageInConfirmationModal: true,
};

/**
 * Hook that evaluates trade form state and returns appropriate message info for user feedback.
 *
 * This hook performs comprehensive validation of trade form inputs and market conditions,
 * returning message objects that inform users about:
 * - Trading restrictions (geoblocking, market status)
 * - Validation errors (invalid prices, sizes, liquidity issues)
 * - Warnings (low volume markets, position limits, account separation)
 * - Informational messages (reduce-only mode, slippage notices)
 *
 * The hook evaluates conditions in priority order, returning the first applicable message.
 * If no issues are found, it returns a base case with no message to display.
 */
export const getMessageInfoForTradeFormState = (
	currentOrderType: UIOrderType,
	maxQuoteAvailable: number,
	stepSize: number,
	tradeForm: DriftStore['tradeForm'],
	priceBoxValue: number,
	currentMarketPrice: number,
	hasCurrentPosition: boolean,
	isGeoblocked: boolean,
	marketSymbol: string | undefined,
	exceedsLiquidity: boolean,
	marketType: MarketType,
	openAddAccountModal: () => void,
	isMaxSpotPositions: boolean,
	isMaxPerpPositions: boolean,
	oraclePrice: BigNum | undefined,
	l2State: L2OrderBook,
	isL2StateLoaded: boolean,
	currentPositionDirection?: 'long' | 'short',
	currentPositionSize?: BigNum,
	basePrecisionToUse?: BN,
	accountName?: string,
	market24hVol?: number,
	marketStatus?: MarketStatus,
	openPositions?: OpenPosition[],
	marketIndex?: number,
	_isMobileScreenSize?: boolean
): MessageInfo => {
	const baseSize = BigNum.fromPrint(
		tradeForm.baseSizeStringValue,
		basePrecisionToUse || BASE_PRECISION_EXP
	);

	if (isGeoblocked) {
		return {
			shouldShowMessage: true,
			messageTitle: 'Limited Functionality',
			message: `You are using Drift from a restricted territory, therefore some functions will be unavailable to you.`,
			messageType: 'error',
			skipMessageInConfirmationModal: false,
		};
	}

	// Check for delisting status - this takes priority over other market status messages
	if (
		isDelistingTimestampInFuture() &&
		(isMarketReduceOnly(marketStatus) ||
			ENUM_UTILS.match(marketStatus, MarketStatus.SETTLEMENT)) &&
		marketIndex !== undefined &&
		marketToBeDelisted(marketIndex)
	) {
		return {
			messageTitle: 'Sunsetting Market',
			shouldShowMessage: true,
			message: getDelistingTextNoPosition(),
			messageType: 'warning',
			skipMessageInConfirmationModal: false,
			disableTradeButton: !hasCurrentPosition,
			messagePosition: 'above-button',
		};
	}

	if (isMarketReduceOnly(marketStatus)) {
		const currentPositionSide =
			currentPositionDirection === 'long' ? 'buy' : 'sell';

		if (
			hasCurrentPosition &&
			currentPositionDirection &&
			currentPositionSide === tradeForm.side
		) {
			return {
				messageTitle: `Market is in Reduce Only mode`,
				shouldShowMessage: true,
				message: `You can only reduce your current position.`,
				messageType: 'warning',
				skipMessageInConfirmationModal: false,
				disableTradeButton: true,
			};
		}

		return {
			messageType: 'info',
			shouldShowMessage: true,
			messageTitle: 'This market is in Reduce Only mode',
			message: `Only orders that reduce existing positions will be allowed.`,
			skipMessageInConfirmationModal: false,
			disableTradeButton: !hasCurrentPosition,
		};
	}

	if (
		isL2StateLoaded &&
		currentOrderType === 'market' &&
		!(l2State.bids.length >= 0 && l2State.asks.length >= 0) // at least one side has no liquidity
	) {
		const hasNoLiquidityAtAll =
			l2State.bids.length === 0 && l2State.asks.length === 0;
		const hasNoSellSideLiquidityButOnBuySideTradeForm =
			l2State.asks.length === 0 && tradeForm.side === 'buy';
		const hasNoBuySideLiquidityButOnSellSideTradeForm =
			l2State.bids.length === 0 && tradeForm.side === 'sell';

		const showNoLiquidityMessage =
			hasNoLiquidityAtAll ||
			hasNoSellSideLiquidityButOnBuySideTradeForm ||
			hasNoBuySideLiquidityButOnSellSideTradeForm;

		if (showNoLiquidityMessage) {
			return {
				messageType: 'error',
				shouldShowMessage: true,
				messageTitle: 'No Liquidity on Orderbook',
				message:
					'There is no liquidity on this market yet. Please use limit orders instead.',
				skipMessageInConfirmationModal: false,
				disableTradeButton: true,
			};
		}
	}

	if (isMaxPerpPositions) {
		return {
			messageType: 'error',
			shouldShowMessage: true,
			messageTitle: `Maximum number of Perp positions reached (${MAX_NUMBER_OF_PERP_POSITIONS})`,
			message: (
				<div>
					To trade in another market, you will need to either settle P&amp;L,
					close one of the existing positions, or cancel open orders.
					Alternatively, you can switch to a new subaccount.
				</div>
			),
			skipMessageInConfirmationModal: false,
			disableTradeButton: true,
		};
	}

	if (isMaxSpotPositions) {
		return {
			messageType: 'error',
			shouldShowMessage: true,
			messageTitle: MAX_SPOT_POSITIONS_ERROR_MESSAGE.title,
			message: MAX_SPOT_POSITIONS_ERROR_MESSAGE.message,
			skipMessageInConfirmationModal: false,
			disableTradeButton: true,
		};
	}

	if (exceedsLiquidity && currentOrderType === 'market') {
		return {
			messageType: 'warning',
			shouldShowMessage: true,
			messageTitle: 'Insufficient Liquidity',
			message: `The order size is too large for the available liquidity. You may want to open a limit order instead.`,
			skipMessageInConfirmationModal: false,
		};
	}

	if (!marketSymbol) {
		return BASE_CASE;
	}

	// show message to suggest using swap if spot market is super low vol
	if (
		ENUM_UTILS.match(marketType, MarketType.SPOT) &&
		market24hVol != undefined &&
		market24hVol < 5000
	) {
		return {
			messageType: 'warning',
			shouldShowMessage: true,
			messageTitle: 'Low Volume Market',
			message: `This market has low 24h volume, you may find a better experience using Swap instead.`,
			skipMessageInConfirmationModal: false,
			disableTradeButton: false,
			messageLink: `https://app.drift.trade/swap/${marketSymbol.toUpperCase()}-USDC`,
			messageLinkDescription: 'Go to Swap',
		};
	}

	if (SUPER_STAKE_ACCOUNT_NAMES.includes(accountName ?? '')) {
		return {
			messageTitle: ``,
			shouldShowMessage: true,
			message: (
				<div className="flex flex-col gap-1">
					<div>
						To easily track your P&amp;L, keep trading activity and Superstaking
						in separate subaccounts.
					</div>
					<Button
						size="sm"
						variant="secondary"
						onClick={openAddAccountModal}
						className="self-start"
					>
						Add subaccount
					</Button>
				</div>
			),
			messageType: 'warning',
			skipMessageInConfirmationModal: false,
			disableTradeButton: false,
		};
	}

	const isBuySide = tradeForm.side === 'buy';

	const secondaryPriceBoxValue = Number(tradeForm.secondaryPriceBoxStringValue);

	// Check errors which apply to market orders only
	if (currentOrderType === 'market') {
		// None of the following error cases apply to market orders
		return BASE_CASE;
	}

	// Case where reduce-only, and has a current position, but the current trade would make current position larger
	// We allow this case for users to preemptively set up their exits before getting into a position
	if (
		tradeForm.reduceOnly &&
		hasCurrentPosition &&
		currentPositionDirection &&
		(currentPositionDirection === 'long' ? 'buy' : 'sell') === tradeForm.side
	) {
		const oppositeOrderSide =
			currentPositionDirection === 'long'
				? ENUM_UTILS.match(marketType, MarketType.SPOT)
					? 'Sell'
					: 'Short'
				: ENUM_UTILS.match(marketType, MarketType.SPOT)
					? 'Buy'
					: 'Long';

		return {
			messageTitle: `Reduce Only Selected`,
			shouldShowMessage: true,
			message: `This order can only fill if you move into a "${oppositeOrderSide}" position. This feature is designed so that you can preemptively set up your exits before getting into a position. If this is not your intention then you may be putting through the wrong order.
			`,
			messageType: 'info',
			skipMessageInConfirmationModal: false,
			disableTradeButton: false,
		};
	}

	// Trade size too large case
	if (
		tradeForm.reduceOnly &&
		currentPositionSize &&
		!currentPositionSize.eq(ZERO) &&
		baseSize.gt(currentPositionSize.abs())
	) {
		return {
			messageTitle: `Trade size too large`,
			shouldShowMessage: true,
			message: `You can't open an order larger than your current position while reduce-only is enabled`,
			messageType: 'error',
			skipMessageInConfirmationModal: false,
			disableTradeButton: true,
		};
	}

	// No trade amount case
	if ((!baseSize || baseSize.eqZero()) && maxQuoteAvailable > 0) {
		return {
			shouldShowMessage: false, // we don't want to be too annoying when the user first visit the trade form; but this condition still needs to be here to prevent other warnings from being shown
			messageTitle: 'Enter a trade amount',
			message: '',
			messageType: 'warning',
			skipMessageInConfirmationModal: false,
			disableTradeButton: false,
		};
	}

	const orderViolationMessage = getMessageInfoForOrderViolation(
		currentOrderType,
		priceBoxValue,
		currentMarketPrice,
		tradeForm.postOnly,
		secondaryPriceBoxValue,
		isBuySide ? 'long' : 'short',
		oraclePrice?.toNum() ?? 0,
		marketSymbol,
		baseSize.toNum(),
		stepSize,
		'tradeForm'
	);

	if (orderViolationMessage) return orderViolationMessage;

	return BASE_CASE;
};
